import { useSnapCarousel } from 'react-snap-carousel';
import { useEffect, useRef } from 'react';
import './styles.scss';

export interface SnapPageCarouselProps {
    /** Number of pages. Caller controls what constitutes a page. */
    pageCount: number;
    /** Render content for the given page index (0-based). */
    renderPage: (pageIndex: number) => React.ReactNode;
    /** Initial page index (e.g. from URL). Applied on mount and when this value changes. */
    initialPage?: number;
    /** Called when the user navigates to a different page (scroll or dot click). */
    onPageChange?: (pageIndex: number) => void;
    /** Optional aria label for the scroll region. */
    'aria-label'?: string;
}

/**
 * A horizontal carousel that snaps to full pages, with an iOS-style page control (dots).
 * Uses native CSS scroll snap and react-snap-carousel for page state. Scroll position
 * snaps to page boundaries; arbitrary in-between scrolling is not possible.
 */
export const SnapPageCarousel = ({
    pageCount,
    renderPage,
    initialPage = 0,
    onPageChange,
    'aria-label': ariaLabel = 'Carousel',
}: SnapPageCarouselProps) => {
    const { scrollRef, activePageIndex, goTo } = useSnapCarousel({ axis: 'x' });
    const lastAppliedInitialPageRef = useRef<number | null>(null);
    const hasSyncedToInitialRef = useRef(false);
    const hasReachedInitialPageRef = useRef(false);

    // Sync scroll position to initialPage when the prop changes (e.g. URL load or use-case reset).
    useEffect(() => {
        if (pageCount <= 0) {
            return;
        }
        const page = Math.max(0, Math.min(initialPage, pageCount - 1));
        if (lastAppliedInitialPageRef.current === page) {
            return;
        }
        lastAppliedInitialPageRef.current = page;
        hasSyncedToInitialRef.current = false;
        hasReachedInitialPageRef.current = false;
        const id = requestAnimationFrame(() => {
            goTo(page);
        });
        // Don't cancel this rAF in cleanup so hasSyncedToInitialRef is always set after goTo.
        // Otherwise effect re-runs (e.g. goTo ref change) cancel it and onPageChange never fires.
        requestAnimationFrame(() => {
            hasSyncedToInitialRef.current = true;
        });
        return () => cancelAnimationFrame(id);
    }, [initialPage, pageCount, goTo]);

    // Notify parent when active page changes (e.g. from user scroll or dot click).
    // Skip until we've synced; skip stale activePageIndex 0 when we're waiting for scroll to initialPage.
    useEffect(() => {
        if (!hasSyncedToInitialRef.current || activePageIndex < 0 || activePageIndex >= pageCount) {
            return;
        }
        if (activePageIndex === initialPage) {
            hasReachedInitialPageRef.current = true;
        }
        if (activePageIndex === 0 && initialPage > 0 && !hasReachedInitialPageRef.current) {
            return;
        }
        onPageChange?.(activePageIndex);
    }, [activePageIndex, initialPage, pageCount, onPageChange]);

    if (pageCount <= 0) {
        return null;
    }

    return (
        <div className="snap-page-carousel">
            <div
                className="snap-page-carousel__scroll"
                ref={scrollRef as React.Ref<HTMLDivElement>}
                role="region"
                aria-label={ariaLabel}
                aria-roledescription="carousel"
            >
                {Array.from({ length: pageCount }, (_, i) => (
                    <div
                        key={i}
                        className="snap-page-carousel__page"
                        role="group"
                        aria-roledescription="slide"
                        aria-label={`Page ${i + 1} of ${pageCount}`}
                    >
                        {renderPage(i)}
                    </div>
                ))}
            </div>
            {pageCount > 1 && (
                <div
                    className="snap-page-carousel__dots"
                    role="tablist"
                    aria-label="Page navigation"
                >
                    {Array.from({ length: pageCount }, (_, i) => (
                        <button
                            key={i}
                            type="button"
                            role="tab"
                            aria-selected={activePageIndex === i}
                            aria-label={`Go to page ${i + 1}`}
                            title={`Page ${i + 1}`}
                            className={`snap-page-carousel__dot ${activePageIndex === i ? 'snap-page-carousel__dot--active' : ''}`}
                            onClick={() => goTo(i)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default SnapPageCarousel;
